import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cn from 'classnames';
import { useStoreContext } from 'store/app';
import present from 'assets/img/presents.png';
import { useResize } from 'src/hooks';
import { QuestTypes } from 'src/types';
import { useTranslationsContext } from 'store/translations';
import './MainBgVideo.styl';
import { get14DaysModalData } from 'utils/get14DaysModalData';
import giftLock from 'assets/img/gift-lock.png';
import { OverlayStarAnimation } from 'components/layout/overlayStarAnimation';
import { Snow } from './Snow';
import { router, routes } from 'utils/router';

export const MainBgVideo = observer(() => {
    const {
        bgVideoPreloaded,
        isMob,
        getVisitedDaysAmount,
        userState,
        questsConfig,
        isLoginsQuestJustComleted,
        logOperation,
        setLoginsModalReward,
        isMainPage,
        isNative
    } = useStoreContext();

    const { height } = useResize();
    const { translate } = useTranslationsContext();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const isMobile = isNative || isMob;

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.onloadeddata = () => {
                setVideoLoaded(true);
            };
        }
    }, []);

    const isPresentHighlighted = useMemo(() => {
        return getVisitedDaysAmount() === 1 || isLoginsQuestJustComleted;
    }, [userState]);

    const isPresentLocked = useMemo(() => {
        const loginsMission = questsConfig?.tasks.find((x) => x.newYearType === QuestTypes.LOGINS);
        if (!loginsMission) return true;

        return !questsConfig?.rewards?.some((x) => x.mission_id === loginsMission.key);
    }, [userState, questsConfig]);

    const onPresentClick = useCallback(() => {
        logOperation('main_page', 'click', 'tree_gift');
        const quest14days = questsConfig?.tasks.find((x) => x.newYearType === QuestTypes.LOGINS);

        if (!quest14days) return;

        if (isPresentLocked) {
            const daysLeft = quest14days.goal! - getVisitedDaysAmount();
            setLoginsModalReward(get14DaysModalData(translate, quest14days, daysLeft, quest14days.goal || 14));
        } else {
            const reward = questsConfig?.rewards?.find((x) => x.mission_id === quest14days.key);
            if (!reward) return;
            if (reward.rolled) {
                setLoginsModalReward(reward);
            } else {
                router.navigate(routes.wheel, { state: { missionId: quest14days.key } });
            }
        }
    }, [isPresentLocked, questsConfig]);

    useEffect(() => {
        if (isMainPage) {
            videoRef?.current?.play();
        } else {
            videoRef?.current?.pause();
        }
    }, [isMainPage]);

    const videoStyles = isMob
        ? { height: height - 100, width: isMobile ? '100%' : 'auto' }
        : { width: '100%', height: '100%' };

    return (
        <div className={cn('main-bg-video-container', isMainPage && 'isVisible')} style={videoStyles}>
            {isMobile ? (
                <Snow />
            ) : (
                <video
                    className="main-bg-video"
                    ref={videoRef}
                    src={bgVideoPreloaded}
                    style={videoStyles}
                    autoPlay
                    playsInline
                    loop
                    muted
                    preload="auto"
                />
            )}
            <div
                className="main-bg-present-container"
                style={
                    isMobile
                        ? {
                              top: '70%',
                              left: '45%'
                          }
                        : {
                              visibility: videoLoaded ? 'visible' : 'hidden'
                          }
                }
            >
                {isPresentHighlighted ? <OverlayStarAnimation className="main-bg-star" small={true} /> : null}
                <img
                    onClick={onPresentClick}
                    className="main-bg-present"
                    style={{ width: isMob ? `calc(${height / 3}px)` : undefined }}
                    src={present}
                    alt="present"
                />
                {isPresentLocked ? (
                    <div className="main-bg-gift-lock">
                        <img className="main-bg-gift-lock-icon" src={giftLock} />
                    </div>
                ) : null}
            </div>
        </div>
    );
});
